// src/pages/RegisterPage.js
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { trackEvent } from '../lib/analytics';
import './AuthPage.css';

const RegisterPage = () => {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();


  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setPrivacyAgreed(checked);
      if (errors.privacyAgreement) {
        setErrors((prev) => ({
          ...prev,
          privacyAgreement: '',
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'الرجاء إدخال الاسم الكامل';
    }

    if (!formData.email) {
      newErrors.email = 'الرجاء إدخال البريد الإلكتروني';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صالح';
    }

    if (!formData.mobile) {
      newErrors.mobile = 'الرجاء إدخال رقم الجوال';
    } else if (!/^05\d{8}$/.test(formData.mobile)) {
      newErrors.mobile = 'رقم الجوال غير صالح. يجب أن يبدأ بـ 05 ويتكون من 10 أرقام';
    }

    if (!formData.password) {
      newErrors.password = 'الرجاء إدخال كلمة المرور';
    } else if (formData.password.length < 8) {
      newErrors.password = 'يجب أن تكون كلمة المرور 8 أحرف على الأقل';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'الرجاء تأكيد كلمة المرور';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'كلمة المرور وتأكيدها غير متطابقين';
    }

    if (!privacyAgreed) {
      newErrors.privacyAgreement = t('register.privacyAgreementError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.REGISTER), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          password: formData.password,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'حدث خطأ أثناء إنشاء الحساب');
      }

      trackEvent('signup_success', { page: '/register', metadata: { source: 'form' } });

      setSubmitSuccess('تم إنشاء الحساب بنجاح. يمكنك الآن تسجيل الدخول.');

      // بعد ثواني يرسله لصفحة تسجيل الدخول
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (err) {
      setSubmitError(err.message || 'حدث خطأ أثناء إنشاء الحساب. الرجاء المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className={`auth-page ${isRTL ? 'rtl' : ''}`}>
        <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">إنشاء حساب جديد</h1>
          <p className="auth-subtitle">
            سجل حسابك في منصتنا لتبدأ رحلتك المعرفية الذكية
          </p>
        </div>

        <div className="auth-form-container">
          {submitError && (
            <div className="auth-error-box">
              {submitError}
            </div>
          )}

          {submitSuccess && (
            <div className="auth-error-box" style={{ background: '#ecfdf3', borderColor: '#bbf7d0', color: '#166534' }}>
              {submitSuccess}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <label htmlFor="name" className="auth-label">
                الاسم الكامل *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                className={`auth-input ${errors.name ? 'auth-input-error' : ''}`}
                value={formData.name}
                onChange={handleChange}
                placeholder="الاسم الكامل"
              />
              {errors.name && <div className="auth-error">{errors.name}</div>}
            </div>

            <div className="auth-input-group">
              <label htmlFor="email" className="auth-label">
                البريد الإلكتروني *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className={`auth-input ${errors.email ? 'auth-input-error' : ''}`}
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
              />
              {errors.email && <div className="auth-error">{errors.email}</div>}
            </div>

            <div className="auth-input-group">
              <label htmlFor="mobile" className="auth-label">
                رقم الجوال *
              </label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                className={`auth-input ${errors.mobile ? 'auth-input-error' : ''}`}
                value={formData.mobile}
                onChange={handleChange}
                placeholder="05xxxxxxxx"
              />
              {errors.mobile && <div className="auth-error">{errors.mobile}</div>}
            </div>

            <div className="auth-input-group">
              <label htmlFor="password" className="auth-label">
                كلمة المرور *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                className={`auth-input ${errors.password ? 'auth-input-error' : ''}`}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
              />
              {errors.password && (
                <div className="auth-error">{errors.password}</div>
              )}
            </div>

            <div className="auth-input-group">
              <label htmlFor="confirmPassword" className="auth-label">
                تأكيد كلمة المرور *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                className={`auth-input ${
                  errors.confirmPassword ? 'auth-input-error' : ''
                }`}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="أعد كتابة كلمة المرور"
              />
              {errors.confirmPassword && (
                <div className="auth-error">{errors.confirmPassword}</div>
              )}
            </div>

            <div className="auth-input-group">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  name="privacyAgreement"
                  checked={privacyAgreed}
                  onChange={handleChange}
                  className="auth-checkbox"
                />
                <span>
                  {t('register.privacyAgreement')}{' '}
                  <Link to="/privacy-policy" className="auth-link" target="_self">
                    {t('footer.privacy')}
                  </Link>
                </span>
              </label>
              {errors.privacyAgreement && (
                <div className="auth-error">{errors.privacyAgreement}</div>
              )}
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading || !privacyAgreed}
            >
              {isLoading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
            </button>
          </form>

          <div className="auth-footer">
            <p className="auth-footer-text">
              لديك حساب بالفعل؟{' '}
              <Link to="/login" className="auth-link-button">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterPage;
